import React, {useState, useContext, useCallback, useEffect} from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';

import {
    Tabs,
    Tab,
    Typography,
    Box,
    Stack,
    Container,
    Grid,
    Card,
    TextField,
    Button,
    Stepper,
    Step,
    StepLabel,
    Paper,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
  } from '@mui/material';

import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadIcon from '@mui/icons-material/Download';
import Label from '../components/label';
import Iconify from '../components/iconify';
import EnvelopeGroupsTable from '../components/envelope-groups-table/index'

import FileInput from '../components/file-input/index'
import CreateEnvelope from '../components/create-envelope/index'
import {GroupList} from '../components/signer-input'

import { EntityContext } from '../entity'
import { isEnvelopeOrBatchCancelable } from '../utils/envelopeAndBatchCancelable'
import { getEnvelopeDeleteErrorMessage } from '../utils/envelopeDeleteError'

// ----------------------------------------------------------------------

const steps = ['Envelope', 'Documentos', 'Grupo de Assinantes', 'Fim'];
const NEW_ENVELOPE_DRAFT_KEY = 'newEnvelopeDraft';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function UpdateEnvelopePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { envelopeKey } = useParams();

  const { getEnvelope, deleteEnvelope } = useContext(EntityContext)

  const [value, setValue] = useState(0);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(0);
  const [done, setDone] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [envelopePath, setEnvelopePatth] = useState('');
  const [envelopeStatus, setEnvelopeStatus] = useState('created');
  const [envelopeName, setEnvelopeName] = useState('');
  const [documentsList, setDocumentsList] = useState([]);
  const [signersList, setSignersList] = useState([]);
  
  const handleChange = (event, newValue) => {
    if (done) return;
    if (newValue === 3) return;
    if (newValue > maxUnlockedStep) return;
    setValue(newValue);
  };

  const changeStep = (newStep) => {
    setValue(newStep)
    setMaxUnlockedStep((prev) => Math.max(prev, newStep))
  }

  const completeJourney = useCallback(() => {
    setDone(true);
    setValue(3);
    setMaxUnlockedStep((prev) => Math.max(prev, 3));
    sessionStorage.removeItem(NEW_ENVELOPE_DRAFT_KEY);
  }, []);

  const normalizeFilename = (name) => {
    if (!name || typeof name !== 'string') return 'envelope';
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s_-]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
      .trim() || 'envelope';
  };

  const handleDownload = (filename) => {

      const finalFilename = `${filename}.pdf`
      downloadFile(envelopePath, finalFilename)
  }

  const downloadFile = (filePath, fileName) => {
      fetch(filePath, {
        method: 'GET'
      })
        .then(response => response.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(new Blob([blob]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
              'download',
              fileName,
            );

          document.body.appendChild(link);

          link.click();

          link.parentNode.removeChild(link);
        });
    };

  const getEnvelopeStatus = useCallback(async () => {
	
    getEnvelope(envelopeKey)
        .then((data) => {
          setEnvelopeStatus(data.envelope.status)
          setEnvelopeName(data.envelope.envelope_name)
          const envPath = (data.envelope.signed_envelope_url ? data.envelope.signed_envelope_url : data.envelope.url)
          setEnvelopePatth(envPath)
          setDocumentsList(data.documents)
          setSignersList(data.groups)
          if (data.envelope.status !== 'created') {
            setDone(true);
            setValue(3);
            setMaxUnlockedStep(3);
          }
        })
	
	  }, [envelopeKey])

  useEffect(() => {
		getEnvelopeStatus()
	  }, [])

  const handleCancelEnvelope = async () => {
    if (!envelopeKey) return;
    setCancelError(null);
    try {
      setIsCanceling(true);
      await deleteEnvelope(envelopeKey);
      navigate('/dashboard/envelopes');
    } catch (error) {
      console.error('Error deleting envelope:', error);
      setCancelError(getEnvelopeDeleteErrorMessage(error));
    } finally {
      setIsCanceling(false);
    }
  };

  const tabDisabled = (index) => done || index > maxUnlockedStep || index === 3;

  return (
    <>
    <Helmet>
        <title> Envelopes </title>
    </Helmet>

    { envelopeStatus === 'created' ? 

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 3 } }}>
        {/* Botão de voltar */}
        <Stack direction="row" alignItems="center" mb={2}>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/dashboard/envelopes')}
            sx={{ mb: 2 }}
          >
            Voltar
          </Button>
        </Stack>

        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: 'background.paper'
          }}
        >
          {/* Stepper para mobile, Tabs para desktop */}
          {isMobile ? (
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={value} alternativeLabel>
                {steps.map((label, index) => (
                  <Step
                    key={label}
                    completed={value > index}
                    disabled={done ? index !== value : index > maxUnlockedStep}
                  >
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          ) : (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={value} 
                onChange={handleChange} 
                aria-label="basic tabs example"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minWidth: { xs: 80, sm: 120 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
              >
                <Tab label="Envelope" {...a11yProps(0)} disabled={tabDisabled(0)}/>
                <Tab label="Documentos" {...a11yProps(1)} disabled={tabDisabled(1)}/>
                <Tab label="Grupo de Assinantes" {...a11yProps(2)} disabled={tabDisabled(2)}/>
                <Tab label="Fim" {...a11yProps(3)} disabled={tabDisabled(3)}/>
              </Tabs>
            </Box>
          )}

          <TabPanel value={value} index={0}>
            <CreateEnvelope changeStep={changeStep} envelopeKey={envelopeKey} />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <FileInput changeStep={changeStep} envelopeKey={envelopeKey} />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <GroupList
              changeStep={changeStep}
              envelopeKey={envelopeKey}
              onEnvelopeSent={completeJourney}
            />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={3}
              sx={{
                py: { xs: 4, sm: 8 },
                minHeight: { xs: 300, sm: 400 }
              }}
            >
              <DoneAllIcon color="success" sx={{ fontSize: { xs: 60, sm: 100 } }} />
              <Typography variant="h5" align="center">
                Envelope enviado para assinatura!
              </Typography>
            </Stack>
          </TabPanel>
        </Paper>
      </Container>

      :
      <>
      <Container>
        {cancelError && (
          <Alert severity="error" onClose={() => setCancelError(null)} sx={{ mb: 2 }}>
            {cancelError}
          </Alert>
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          mb={5}
          spacing={2}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Envelope: {envelopeKey}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
            {isEnvelopeOrBatchCancelable(envelopeStatus) && (
              <Button
                variant="outlined"
                color="error"
                disabled={isCanceling}
                startIcon={
                  isCanceling ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <Iconify icon="eva:trash-2-outline" />
                  )
                }
                onClick={handleCancelEnvelope}
              >
                {isCanceling ? 'Cancelando...' : 'Cancelar'}
              </Button>
            )}
            <Button
              sx={{ m: { xs: 0, sm: 2 } }}
              variant="contained"
              component="label"
              startIcon={<DownloadIcon />}
              onClick={(_) => handleDownload(normalizeFilename(envelopeName))}
            >
              Baixar
            </Button>
          </Stack>
        </Stack>
        <Card >
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.2} sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Informações
            </Typography>
          </Stack>
          <Stack spacing={2} sx={{ p: 3 }}>
            <Typography variant="subtitle2" noWrap>
              Nome: {envelopeName}
            </Typography>
            <Typography variant="subtitle2" noWrap>
              Quantidade de documentos: {documentsList.length}
            </Typography>
            <Typography variant="subtitle2" noWrap>
              Status: <Label>{envelopeStatus}</Label>
            </Typography>
          </Stack>
        </Card>
      </Container>
        <EnvelopeGroupsTable signersList={signersList}/>
      </>
    }


    </>
  );
}
